import {
  Divider,
  IconBulletList,
  Stack,
  TableCell,
  TableRow,
  Text,
} from 'braid-design-system';
import type { GraphQLEnumType, GraphQLEnumValue } from 'graphql';
import { InlineCode } from 'scoobie';

import { SchemaSection } from '#src/components/SchemaSection/SchemaSection.tsx';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import { DeprecationWarning } from '../components/DeprecationWarning.tsx';
import { SchemaMarkdown } from '../components/SchemaMarkdown.tsx';
import { Strikethrough } from '../components/Strikethrough.tsx';
import { useLocation } from 'react-router';

const EnumValueDetails = ({
  enumName,
  value,
}: {
  enumName: string;
  value: GraphQLEnumValue;
}) => (
  <TableRow>
    <TableCell width="content">
      <Text
        tone={
          typeof value.deprecationReason === 'string' ? 'secondary' : undefined
        }
        weight="strong"
      >
        <Strikethrough strike={typeof value.deprecationReason === 'string'}>
          <InlineCode weight="weak">{value.name}</InlineCode>
        </Strikethrough>
      </Text>
    </TableCell>

    <TableCell wrap>
      <Stack space="medium">
        {typeof value.deprecationReason === 'string' && (
          <DeprecationWarning type={`${enumName}.${value.name}.deprecation`} />
        )}

        <SchemaMarkdown type={`${enumName}.${value.name}`} />
      </Stack>
    </TableCell>
  </TableRow>
);

export const EnumSchemaPage = ({ enumType }: { enumType: GraphQLEnumType }) => (
  <BasePage
    title={{
      children: <InlineCode weight="weak">{enumType.name}</InlineCode>,
      htmlTitle: enumType.name,
      stringTitle: enumType.name,
      subHeading: (
        <Text
          icon={<IconBulletList />}
          size="small"
          weight="medium"
          tone="secondary"
        >
          Enum
        </Text>
      ),
    }}
    breadcrumbs={[
      { href: '/schema', title: 'Schema' },
      { href: useLocation().pathname, title: enumType.name },
    ]}
  >
    <SchemaMarkdown type={enumType.name} />

    <Divider />

    <SchemaSection heading="Enum values">
      {enumType.getValues().map((value) => (
        <EnumValueDetails
          key={value.name}
          enumName={enumType.name}
          value={value}
        />
      ))}
    </SchemaSection>
  </BasePage>
);
